// ============================================================================
// QuantumAI Shield — Stripe Checkout Component
// File: src/components/StripeCheckout.jsx  (React dashboard on Vercel)
//
// PURPOSE: Handles the full checkout flow:
//   1. User clicks "Upgrade" on a plan
//   2. This component calls POST /api/stripe/create-checkout-session
//   3. On success, redirects window.location to Stripe hosted checkout URL
//   4. On return, reads ?payment=success or ?payment=cancelled from URL
//
// SECURITY:
//   - API call goes to Oracle VM via VITE_API_URL env var — never hardcoded
//   - Bearer token from Supabase session — same as all other authenticated calls
//   - priceId sent to backend which validates against server-side allowlist
//   - No payment data ever touches the React app — Stripe hosts the payment page
// ============================================================================
import { useState, useEffect } from 'react';
import { STRIPE_PLANS } from '../lib/stripeConfig';
import { supabase } from '../lib/supabaseClient.js';

const blinkKeyframes = `
@keyframes blinkCyan {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
}
`;

if (typeof document !== 'undefined' && !document.getElementById('blink-cyan-style')) {
    const styleTag = document.createElement('style');
    styleTag.id = 'blink-cyan-style';
    styleTag.innerHTML = blinkKeyframes;
    document.head.appendChild(styleTag);
}



// API base URL — set VITE_API_URL=https://api.quantumaishield.com in Vercel env vars
const API_URL = import.meta.env.VITE_API_URL || 'https://api.quantumaishield.com';

// ── useStripeReturn — detect return from Stripe checkout ─────────────────────
// Reads ?payment=success or ?payment=cancelled from URL on mount
// Returns { paymentStatus: 'success' | 'cancelled' | null }
export function useStripeReturn() {
    const [paymentStatus, setPaymentStatus] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get('payment');

        if (status === 'success' || status === 'cancelled') {
            setPaymentStatus(status);
            // Clean the query param from URL without page reload
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    return { paymentStatus };
}

// ── StripeCheckoutButton — single plan checkout button ───────────────────────
// Props:
//   planKey       — key from STRIPE_PLANS ('starter' | 'professional' | 'enterprise' | 'qledger')
//   session       — Supabase session object (must have access_token)
//   onError       — callback(errorMessage: string) for parent to handle errors
//   className     — optional CSS class override
//   children      — button label (defaults to "Upgrade to {planName}")
//
//   country         — (optional) controlled country value. Pass this + onCountryChange
//                      together to let a parent (e.g. UpgradeScreen) enforce
//                      "only one plan card selected at a time" across siblings.
//   onCountryChange — (optional) controlled country change handler.
//                      If either of the two above is omitted, this component
//                      falls back to its own local state (uncontrolled) —
//                      preserves existing behavior for StripeCheckoutCard below.


export function StripeCheckoutButton({
    planKey,
    session,
    onError,
    className = '',
    compact = false,
    children,
    country: controlledCountry,
    onCountryChange,
}) {
    const [loading, setLoading] = useState(false);
    const [localCountry, setLocalCountry] = useState(''); // empty until user explicitly selects

    // Controlled mode (parent manages selection across sibling cards) vs.
    // uncontrolled mode (this component manages its own state, unchanged
    // behavior for any existing caller that doesn't pass these two props).
    const isControlled = controlledCountry !== undefined && typeof onCountryChange === 'function';
    const country       = isControlled ? controlledCountry : localCountry;
    const setCountry    = isControlled ? onCountryChange : setLocalCountry;

    // Fix: after window.location.href navigates to Stripe/Paddle, some
    // browsers restore this exact page (and its frozen in-memory React
    // state) from the back-forward cache instead of reloading fresh when
    // the user returns (closes the checkout tab / hits back). Without this,
    // "loading" stays stuck true forever, since the success path never
    // resets it (only the error path does — see catch block below).
    // The `pageshow` event with `event.persisted === true` fires exactly
    // when that bfcache restore happens, so we use it to force loading
    // back to false and let the user try again cleanly.
    //
    // NOTE: this must run before the `if (!plan) return null;` check below —
    // React Hooks must be called in the same order on every render, so no
    // hook can come after a conditional early return.
    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) setLoading(false);
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    const plan = STRIPE_PLANS[planKey];

    if (!plan) {
        console.error(`StripeCheckoutButton: unknown planKey "${planKey}"`);
        return null;
    }

        async function handleCheckout() {
        if (loading) return; // prevent double-click
        if (!country) return; // require explicit country selection first

        setLoading(true);

        try {
            // Fix: fetch a FRESH session right here, at click-time, instead of
            // trusting the `session` prop — that prop is captured once when the
            // parent (UpgradeScreen/TrialBanner) mounts, and its access_token can
            // go stale (JWTs expire in ~1hr) if the user sits on the page a
            // while before clicking Upgrade. supabase-js auto-refreshes the
            // token under the hood as long as a valid refresh token exists, so
            // this always sends a live, valid Bearer token — this is what was
            // producing the "Invalid user token" backend error.
            const { data: { session: freshSession } } = await supabase.auth.getSession();

            if (!freshSession?.access_token) {
                throw new Error('You must be logged in to upgrade.');
            }

            // NOTE: calls the unified router (Stripe for US, Paddle otherwise) —
            // no longer calls /api/stripe/create-checkout-session directly.
            // planKey ('starter' | 'professional' | 'qledger') matches
            // PLAN_CONFIG keys in checkout-router.js exactly.
            const response = await fetch(`${API_URL}/api/checkout/create-session`, {
                method:  'POST',
                headers: {
                    'Content-Type':  'application/json',
                    'Authorization': `Bearer ${freshSession.access_token}`,
                },
                body: JSON.stringify({ planKey, country }),
            });



            // Parse response safely — server may return non-JSON on error
            let data;
            try {
                data = await response.json();
            } catch {
                throw new Error('Invalid response from server');
            }

            if (!response.ok || !data.success) {
                throw new Error(data.error || `Server error: ${response.status}`);
            }

            if (!data.url) {
                throw new Error('No checkout URL returned from server');
            }

            // Redirect to Stripe hosted checkout — no payment data in our app
            window.location.href = data.url;

        } catch (err) {
            console.error('[StripeCheckout] error:', err.message);
            if (onError) onError(err.message || 'Checkout failed. Please try again.');
            setLoading(false); // only reset on error — success navigates away
        }
    }


    return (
        <>

        {!country && (
            <p style={{
                color:         '#00D9FF',
                fontWeight:    700,
                fontSize:      compact ? '12px' : '15px',
                textAlign:     'center',
                marginBottom:  compact ? '4px' : '6px',
                lineHeight:    '1.3',
                animation:     'blinkCyan 1s ease-in-out infinite',
            }}>
                ⚠ Select Your Country
            </p>
        )}


        <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            aria-label="Billing country"
            style={{
                width:        '100%',
                marginBottom: '8px',
                padding:      '8px',
                borderRadius: '8px',
                border:       '1px solid rgba(255,255,255,0.15)',
                background:   '#0f172a',
                color:        '#f1f5f9',
                fontSize:     '13px',
            }}
        >
            <option value="" disabled style={{ background: '#0f172a', color: '#94a3b8' }}>Select your country...</option>
            <option value="US" style={{ background: '#0f172a', color: '#f1f5f9' }}>United States</option>
            <option value="INTL" style={{ background: '#0f172a', color: '#f1f5f9' }}>Outside the United States</option>
        </select>

        <button
            onClick={handleCheckout}
            disabled={loading || !country}
            aria-label={`Upgrade to ${plan.name} plan`}
            aria-busy={loading}
            style={{
                background:    (!country) ? '#334155' : 'linear-gradient(to right, #00ccff, #0077ff)',
                color:         (!country) ? '#64748b' : '#ffffff',
                border:        'none',
                borderRadius:  '12px',
                padding:       '12px 16px',
                fontSize:      '14px',
                fontWeight:    700,
                cursor:        (loading || !country) ? 'not-allowed' : 'pointer',
                width:         '100%',
                letterSpacing: '0.01em',
                boxShadow:     (!country) ? 'none' : '0 4px 14px rgba(0,204,255,0.35)',
                opacity:       loading ? 0.7 : 1,
                marginTop:     'auto',
            }}
        >

            {loading ? (
                <span className="stripe-checkout-btn__loading">
                    <span className="stripe-checkout-btn__spinner" aria-hidden="true" />
                    Redirecting to checkout…
                </span>
            ) : (
                children || `Upgrade to ${plan.name}`
            )}
        </button>
        </>
    );
}

// ── StripePaymentBanner — success / cancelled return banner ───────────────────
// Show this at the top of the dashboard when user returns from Stripe
// Props:
//   status  — 'success' | 'cancelled' | null
//   onDismiss — callback to clear the banner
export function StripePaymentBanner({ status, onDismiss }) {
    if (!status) return null;

    const isSuccess   = status === 'success';
    const bgColor     = isSuccess ? '#0f2a1a' : '#2a1a0f';
    const borderColor = isSuccess ? '#22c55e' : '#f59e0b';
    const icon        = isSuccess ? '✅' : '⚠️';
    const message     = isSuccess
        ? 'Payment successful! Your account has been upgraded. It may take a moment to reflect.'
        : 'Checkout was cancelled. Your plan has not changed.';

    return (
        <div
            role="alert"
            aria-live="polite"
            style={{
                background:   bgColor,
                border:       `1px solid ${borderColor}`,
                borderRadius: '8px',
                padding:      '12px 16px',
                marginBottom: '16px',
                display:      'flex',
                alignItems:   'center',
                gap:          '10px',
                fontSize:     '14px',
                color:        '#e2e8f0',
            }}
        >
            <span aria-hidden="true">{icon}</span>
            <span style={{ flex: 1 }}>{message}</span>
            {onDismiss && (
                <button
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    style={{
                        background: 'transparent',
                        border:     'none',
                        color:      '#94a3b8',
                        cursor:     'pointer',
                        fontSize:   '18px',
                        lineHeight: 1,
                        padding:    '0 4px',
                    }}
                >
                    ×
                </button>
            )}
        </div>
    );
}

// ── StripeCheckoutCard — full plan card with price and checkout button ─────────
// Props:
//   planKey    — key from STRIPE_PLANS
//   session    — Supabase session object
//   isPopular  — show "POPULAR" badge
//   onError    — error callback
export function StripeCheckoutCard({ planKey, session, isPopular = false, onError }) {
    const plan = STRIPE_PLANS[planKey];
    if (!plan) return null;

    return (
        <div
            style={{
                background:   isPopular ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.04)',
                border:       isPopular ? '2px solid #6366f1' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding:      '24px',
                display:      'flex',
                flexDirection:'column',
                gap:          '16px',
            }}
        >
            {isPopular && (
                <span style={{
                    background:   '#6366f1',
                    color:        '#fff',
                    fontSize:     '11px',
                    fontWeight:   700,
                    letterSpacing:'0.08em',
                    padding:      '3px 10px',
                    borderRadius: '20px',
                    alignSelf:    'flex-start',
                }}>
                    POPULAR
                </span>
            )}

            <div>
                <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 4px' }}>
                    {plan.description}
                </p>
                <h3 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: 700, margin: '0 0 4px' }}>
                    {plan.name}
                </h3>
                <p style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: 800, margin: 0 }}>
                    {plan.amount}
                    <span style={{ fontSize: '14px', fontWeight: 400, color: '#94a3b8', marginLeft: '6px' }}>
                        {plan.period}
                    </span>
                </p>
                {plan.monthlyEq && (
                    <p style={{ color: '#22c55e', fontSize: '13px', margin: '4px 0 0' }}>
                        Annual only · {plan.monthlyEq}
                    </p>
                )}
            </div>

            <StripeCheckoutButton
                planKey={planKey}
                session={session}
                onError={onError}
                style={{
                    background:   isPopular ? '#6366f1' : 'rgba(255,255,255,0.08)',
                    color:        '#fff',
                    border:       'none',
                    borderRadius: '8px',
                    padding:      '12px 20px',
                    fontSize:     '14px',
                    fontWeight:   600,
                    cursor:       'pointer',
                    width:        '100%',
                }}
            >
                {plan.mode === 'payment' ? `Purchase ${plan.name}` : `Upgrade to ${plan.name}`}
            </StripeCheckoutButton>
        </div>
    );
}